"use client";

import { CircleCloseModalButton } from "@/components/atoms/CircleCloseModalButton";
import { getNoise } from "@/utils/noise";
import { MIN_SOCIAL_PROOF, MAX_SOCIAL_PROOF } from "@/constants/noise";
import Image from "next/image";
import { useCallback, useState, useEffect } from "react";
import classNames from "classnames";
import { Inter } from "next/font/google";
import localFont from "next/font/local";
import { PorangeButton } from "../atoms/PorangeButton";
import { SocialProofAvatars } from "../molecules/SocialProofAvatars";
import { CircleCheckIcon } from "../atoms/CircleCheckIcon";
import {
    trackModalShown,
    trackModalCtaClicked,
    trackModalDismissed,
} from "@/utils/mixpanel";
import { useSearchParams } from "next/navigation";
import { deleteSearchParams, setSearchParams } from "@/utils/url";
import { MixpanelValues } from "@/constants/mixpanel";

const inter = Inter({ subsets: ["latin"] });
const montFont = localFont({ src: "../../fonts/Mont-Bold.ttf" });

const GenericModalVariations = {
    A: {
        title: "Access over 179k gluten-free places, worldwide!",
        description:
            "Find trusted gluten-free restaurants, cafés, and bakeries, constantly reviewed for safety and accuracy.",
        ctaText: "EXPLORE THE MAP",
        version: "General Test A",
        headerImage: "/assets/gluten-free/modal-header-generic-a.png",
    },
    B: {
        title: "Access detailed info for over 179k gluten-free spots",
        ctaText: "EXPLORE THE MAP",
        headerImage: "/assets/gluten-free/modal-header-generic-b.png",
        headerImagePadding: true,
        version: "General Test B",
        checkList: [
            "Updated daily for accuracy",
            "Every place ranked by safety",
            "Filter by Dairy-Free, Kid-Friendly & more",
        ],
    },
};

const CommunityModalVariations = {
    A: {
        title: "179k gluten-free spots, curated by the community",
        description:
            "Join 454k gluten-free foodies who’ve found freedom by discovering safe, trusted dining spots around the world.",
        ctaText: "JOIN THE COMMUNITY",
        headerImage: "/assets/gluten-free/modal-header-community.png",
        version: "Community Test A",
    },
    B: {
        title: "179k gluten-free spots, curated by the community",
        ctaText: "JOIN THE COMMUNITY",
        headerImage: "/assets/gluten-free/modal-header-community.png",
        version: "Community Test B",
        checkList: [
            "454k+ gluten-free contributors",
            "1.1M+ reviews",
            "179k places worldwide",
        ],
    },
};

export const GlutenFreeModal = ({
    link,
    area,
    isMobile,
    places = [],
    quizFlowControllerVariation,
    isNewFlow = false,
}) => {
    const [activeModal, setActiveModal] = useState(null);
    const searchParams = useSearchParams();

    const noise = getNoise("glutenfreeq", {
        min: MIN_SOCIAL_PROOF,
        max: MAX_SOCIAL_PROOF,
        range: MAX_SOCIAL_PROOF - MIN_SOCIAL_PROOF,
    });

    const triggerGenericModal = useCallback(() => {
        console.log({ isMobile, isNewFlow });
        if (isMobile && isNewFlow) {
            console.count("generic modal click");
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set("modal", "generic");
            window.history.pushState({}, "", `?${searchParams.toString()}`);
            return;
        }

        // disable document scroll
        document.body.style.overflow = "hidden";
        const storedVariation = localStorage.getItem("generic-modal-variation");

        if (storedVariation) {
            setActiveModal(GenericModalVariations[storedVariation]);
            trackModalShown(
                GenericModalVariations[storedVariation].version,
                GenericModalVariations[storedVariation].ctaText
            );
        } else {
            const randomVariation = Math.floor(Math.random() * 2);
            const variationKey = randomVariation === 1 ? "A" : "B";
            const variation = GenericModalVariations[variationKey];
            setActiveModal(variation);
            trackModalShown(variation.version, variation.ctaText);
            localStorage.setItem("generic-modal-variation", variationKey);
        }
    }, [isMobile, isNewFlow]);

    const triggerCommunityModal = useCallback(() => {
        console.log({ isMobile, isNewFlow });
        if (isMobile && isNewFlow) {
            console.count("community modal click");
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set("modal", "community");
            window.history.pushState({}, "", `?${searchParams.toString()}`);
            return;
        }

        document.body.style.overflow = "hidden";

        const storedVariation = localStorage.getItem("community-modal-variation");

        if (storedVariation) {
            setActiveModal(CommunityModalVariations[storedVariation]);
            trackModalShown(
                CommunityModalVariations[storedVariation].version,
                CommunityModalVariations[storedVariation].ctaText
            );
        } else {
            const randomVariation = Math.floor(Math.random() * 2);
            const variationKey = randomVariation === 1 ? "A" : "B";
            const variation = CommunityModalVariations[variationKey];
            setActiveModal(variation);
            trackModalShown(variation.version, variation.ctaText);
            localStorage.setItem("community-modal-variation", variationKey);
        }
    }, [isMobile, isNewFlow]);

    useEffect(() => {
        const trigger = searchParams.get("trigger");
        const triggerValue = decodeURIComponent(trigger);

        console.log({ trigger, triggerValue });

        switch (triggerValue) {
            case MixpanelValues.ItemTapped.NAME:
            case MixpanelValues.ItemTapped.AWARD_EMBLEM:
            case MixpanelValues.ItemTapped.ADDRESS:
            case MixpanelValues.ItemTapped.SAFETY_RATING:
            case MixpanelValues.ItemTapped.IMAGE:
            case MixpanelValues.ItemTapped.TAGS:
            case MixpanelValues.ItemTapped.LEARN_MORE:
            case MixpanelValues.ItemTapped.VIEW_MENU:
                deleteSearchParams([{ key: "trigger", value: "" }]);
                triggerGenericModal();
                break;

            case MixpanelValues.ItemTapped.GLUTEN_FREE_COMMUNITY:
            case MixpanelValues.ItemTapped.GLUTEN_FREE_REC_COUNT:
            case MixpanelValues.ItemTapped.REVIEW_TEXT:
                deleteSearchParams([{ key: "trigger", value: "" }]);
                triggerCommunityModal();
                break;
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [searchParams]);

    if (!activeModal)
        return null;

    return (
        <div className="fixed z-20 inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div
                className={classNames(
                    inter.className,
                    `relative bg-white rounded-[20px] overflow-hidden 
					shadow-lg 
					max-w-sm w-full
					mx-6 text-blue-900
					min-h-fit max-h-[calc(100vh-90px)] h-[488px]
					`
                )}>
                <button
                    className="absolute top-6 right-6 text-gray-500 hover:text-gray-700"
                    onClick={() => {
                        setActiveModal(null);
                        trackModalDismissed(activeModal.version, activeModal.ctaText);
                        document.body.style.overflow = "auto";
                    }}>
                    <CircleCloseModalButton />
                </button>
                <div
                    className={classNames({
                        "flex justify-center": true,
                        "p-4 pb-0": activeModal.headerImagePadding,
                        "pb-5": !activeModal.headerImagePadding,
                    })}>
                    <Image
                        alt={activeModal.title}
                        src={activeModal.headerImage}
                        className="w-full"
                        style={{ objectFit: "cover" }}
                        width={624}
                        height={476}
                    />
                </div>
                <div className="p-4">
                    <h1
                        className={classNames(
                            montFont.className,
                            "text-[21px] text-left mb-2 leading-[27px]"
                        )}>
                        {activeModal.title}
                    </h1>
                    {activeModal.description ? (
                        <p className="text-left mb-4 text-[13px] font-medium">
                            {activeModal.description}
                        </p>
                    ) : null}
                    {activeModal.checkList ? (
                        <ul className="mb-4 flex flex-col gap-3">
                            {activeModal.checkList.map((item, index) => (
                                <li
                                    key={index}
                                    className="flex items-center gap-2 text-[13px] font-medium">
                                    <CircleCheckIcon />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <div className="mt-7">
                        <PorangeButton
                            id="gluten-free-modal-cta"
                            onClick={() => {
                                trackModalCtaClicked({
                                    quizFlowControllerVariation,
                                    modalName: activeModal.version,
                                    buttonCta: activeModal.ctaText,
                                    isMobile,
                                    link,
                                    area,
                                });
                            }}
                            className={classNames({
                                "h-[60px] rounded-[20px] text-lg w-full cursor-pointer": true,
                                "opacity-50 pointer-events-none": !link,
                                "opacity-100 pointer-events-auto": link,
                            })}>
                            {activeModal.ctaText}
                        </PorangeButton>
                        <div className="flex gap-2 justify-center mt-2 text-[13px] font-semibold">
                            <SocialProofAvatars
                                count={3}
                                avatarsBorderColor="white"
                            />
                            <span>{noise.value} joined today 👆</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
